import { PerkHelpers, PerkInitializer } from "../library/perks/Perk";
import { SkillHelpers, SkillInitializer } from "../library/skills/Skill";
import { Kinship } from "./Kinships";
import { RhetoricInitializer } from "../library/socials/Rhetoric";
import { Personality } from "../library/socials/Personality";
import { ItemHelpers } from "../library/items/Item";
import { SortedSet } from "../data-structures/SortedSet";
import { MinionInitializer } from "../library/minions/Minion";
import { ItemInitializer, ModInitializer } from "../library/items/ItemInitializers";
import { OwnedCompetencyReference, OwnedItemReference, OwnedMinionReference,
        OwnedModReference,
        OwnedReference, OwnedRhetoricReference, OwnedSkillReference } from "../Ownership";
import { DefaultWornLimit, WornLimits } from "./WornLimits";
import { Condition } from "./Conditions";
import { CharacterAbility, GetCharacterAbilityKey } from "../abilities/Ability";
import { CustomCompetencyInitializer, CompetencyInitializer } from "./Competencies";
import { ModSlot } from "../library/items/mods/ModSlot";
import { CharacterDice, RollRequest } from "../rolls/Roll";
import { SkillName } from "../library/skills/SkillMap";
import { ActiveEditingHashmap } from "../ActiveEditingHashmap";
import { ItemFilterInitializer } from "../filters/ItemFilter";
import { CommunalDice } from "../adventures/Adventure";

export type CreateCharacterRequest = {
    name: string;
}

export type CharacterInitializer = {
    id: string;
    ownerId: string;
    name: string;
    created: string;
    kinship: Kinship;
    level: number;
    className: string;
    enforceRules: boolean;
    money: number;

    adjustMaxHealth?: number;
    overrideMaxHealth?: number;

    currentHealth: number;
    currentWounds: number;
    juiced: boolean;

    currentDice: CharacterDice;

    customCompetencies: CustomCompetencyInitializer[];
    competencies: OwnedCompetencyReference[];
    conditions: string[];

    baseMovement: number;
    movementAdjust: number;
    movementOverride?: number;

    skills: OwnedSkillReference[];
    rhetorics: OwnedRhetoricReference[];
    personalities: OwnedReference[];
    perks: OwnedReference[];

    bodySlots: ModSlot[];
    bodyMods: OwnedModReference[];

    activeMods: OwnedModReference[];

    held: OwnedItemReference[];
    worn: OwnedItemReference[];
    loose: OwnedItemReference[];

    minions: OwnedMinionReference[];
}

export type CharacterItemCollection = Pick<LocalCharacterInitializer, 'held'>
    | Pick<LocalCharacterInitializer, 'worn'>
    | Pick<LocalCharacterInitializer, 'loose'>
    | Pick<LocalCharacterInitializer, 'bodyMods'>
    | Pick<LocalCharacterInitializer, 'activeMods'>;
export type CharacterItemSuite = Partial<Pick<LocalCharacterInitializer, 'held' | 'worn' | 'loose' | 'bodyMods' | 'activeMods'>>;
export type CharacterItemCollectionName = keyof CharacterItemSuite;

export type CharacterItemSearchResult = {
    collectionName: CharacterItemCollectionName;
    reference: OwnedItemReference;
}

export type LocalCharacterInitializer = CharacterInitializer & {
    rollRequest?: RollRequest;
    previewItemInstanceId?: string;
    previewSlotId?: string;
    previewSlotItemInstanceId?: string;
    pendingProperties: ActiveEditingHashmap;
    showItemAdder: boolean;
    itemFilter: ItemFilterInitializer;
    communalDice: CommunalDice;
    deleting: boolean;
}

const getSkillLevel = (character: CharacterInitializer, skillName: SkillName) => {
    const skill = character.skills.find(s => s.id == SkillHelpers.IdToNameMap[skillName]);
    if (skill == null) {
        throw `Skill "${skillName}" not found on character ${character.id}.`;
    }

    return SkillHelpers.getAdjustedLevel(skill);
}

const getMaxHordeSize = (character: CharacterInitializer) => {
    const commandSkillLevel = getSkillLevel(character, 'Command');
    if (commandSkillLevel < 2) {
        return 0;
    }
    if (commandSkillLevel <= 6) {
        return 1;
    }
    if (commandSkillLevel <= 10) {
        return 2;
    }
    return 3;
}


const getSkillPointsForLevel = (character: CharacterInitializer) => {
    return character.level * SkillHelpers.PointsPerLevel;
}

const getSkillPointsUsed = (character: CharacterInitializer) => {
    return character.skills.sum(s => SkillHelpers.getSkillPointsUsed(s));
}

const getRemainingSkillPoints = (character: CharacterInitializer) => {
    const skillPointsForLevel = getSkillPointsForLevel(character);
    const skillPointsUsed = getSkillPointsUsed(character);
    return Math.max(skillPointsForLevel - skillPointsUsed, 0);
}

const getMaxHealth = (character: CharacterInitializer) => {
    const athletics = getSkillLevel(character, 'Athletics');
    const melee = getSkillLevel(character, 'Melee');
    if (athletics == null || melee == null) {
        throw "Character missing critical skills.";
    }

    const greaterSkill = athletics > melee ? athletics : melee;

    let skillModifier: number = 2;
    if (greaterSkill >= 6) {
        skillModifier = 3;
    }
    else if (greaterSkill >= 10) {
        skillModifier = 4;
    }

    return 15 + (3 * character.level) + greaterSkill * skillModifier;
}

const getEquipment = (character: CharacterInitializer) => {
    return [...character.held, ...character.worn, ...character.bodyMods];
}

const getInventory = (character: CharacterInitializer) => {
    return [...character.held, ...character.worn, ...character.bodyMods, ...character.activeMods, ...character.loose];
}

const searchItemByInstanceId = (character: CharacterInitializer, itemInstanceId: string): CharacterItemSearchResult | null => {
    for (let heldItem of character.held) {
        if (heldItem.instanceId == itemInstanceId) {
            return {
                collectionName: 'held',
                reference: heldItem
            };
        }
    }

    for (let wornItem of character.worn) {
        if (wornItem.instanceId == itemInstanceId) {
            return {
                collectionName: 'worn',
                reference: wornItem
            };
        }
    }

    for (let looseItem of character.loose) {
        if (looseItem.instanceId == itemInstanceId) {
            return {
                collectionName: 'loose',
                reference: looseItem
            };
        }
    }

    for (let mod of character.activeMods) {
        if (mod.instanceId == itemInstanceId) {
            return {
                collectionName: 'activeMods',
                reference: mod
            };
        }
    }

    for (let mod of character.bodyMods) {
        if (mod.instanceId == itemInstanceId) {
            return {
                collectionName: 'bodyMods',
                reference: mod
            };
        }
    }

    return null;
}

const getItemCollection = (character: CharacterInitializer, reference: OwnedItemReference): CharacterItemCollectionName | null => {
    if (character.held.any(i => i.instanceId == reference.instanceId)) {
        return 'held';
    }
    if (character.worn.any(i => i.instanceId == reference.instanceId)) {
        return 'worn';
    }
    if (character.loose.any(i => i.instanceId == reference.instanceId)) {
        return 'loose';
    }
    if (character.activeMods.any(i => i.instanceId == reference.instanceId)) {
        return 'activeMods';
    }
    if (character.bodyMods.any(i => i.instanceId == reference.instanceId)) {
        return 'bodyMods';
    }
    return null;
}

const removeItems = (character: CharacterInitializer, removeInstanceIds: string[]): [CharacterItemSuite, OwnedItemReference[]] => {
    let removed: OwnedItemReference[] = [];
    for (let item of character.held) {
        if (removeInstanceIds.any(rid => rid == item.instanceId)) {
            removed.push(item);
        }
    }
    const held = character.held.filter(i => !removeInstanceIds.any(r => r == i.instanceId));
    
    for (let item of character.worn) {
        if (removeInstanceIds.any(rid => rid == item.instanceId)) {
            removed.push(item);
        }
    }
    const worn = character.worn.filter(i => !removeInstanceIds.any(r => r == i.instanceId));
    
    for (let item of character.bodyMods) {
        if (removeInstanceIds.any(rid => rid == item.instanceId)) {
            removed.push(item);
            delete item.assignedItemId;
            delete item.assignedSlotId;
        }
    }
    const bodyMods = character.bodyMods.filter(i => !removeInstanceIds.any(r => r == i.instanceId));

    for (let item of character.activeMods) {
        if (removeInstanceIds.any(rid => rid == item.instanceId)) {
            removed.push(item);
            delete item.assignedItemId;
            delete item.assignedSlotId;
        }
    }
    const activeMods = character.activeMods.filter(i => !removeInstanceIds.any(r => r == i.instanceId));
    
    //Items removed from loose do NOT get added anywhere else.
    const loose = character.loose.filter(i => !removeInstanceIds.any(r => r == i.instanceId));

    const suite = {
        held,
        worn,
        loose,
        activeMods,
        bodyMods,
    };
    return [suite, removed];
}

const couldHoldItem = (character: CharacterInitializer, allItems: ItemInitializer[], reference: OwnedItemReference, toReplace: OwnedItemReference[] = []) => {
    const handsOccupied = character.held
        .filter(held => held.instanceId != reference.instanceId)
        .filter(held => !toReplace.any(tr => tr.instanceId == held.instanceId))
        .sum(held => ItemHelpers.getTotalHandsUsed(allItems, held, character.activeMods));
    const itemHands = ItemHelpers.getTotalHandsUsed(allItems, reference, character.activeMods);
    return handsOccupied + itemHands <= CharacterHelpers.MaxHands;
}

const getWornLimitForSlot = (slotName: string) => WornLimits[slotName as keyof typeof WornLimits] ?? DefaultWornLimit;

const couldWearItem = (character: CharacterInitializer, allItems: ItemInitializer[], reference: OwnedItemReference, toReplace: OwnedItemReference[] = []) => {
    const toWear = allItems.find(i => i.id == reference.id);
    if (toWear == null) {
        throw "Requested to check wearability of nonexistent item.";
    }

    if (!toWear.wornOn.any()) {
        return false;
    }

    const currentlyWornToStillBeWorn = character.worn
        .filter(worn => worn.instanceId != reference.instanceId)
        .filter(worn => !toReplace.any(tr => tr.instanceId == worn.instanceId))
        .map(worn => allItems.find(i => i.id == worn.id))
        .filter(worn => worn != null);
    for (let slotName of toWear.wornOn) {
        const wornOccupyingSlot = currentlyWornToStillBeWorn.count(cw => cw.wornOn.contains(slotName));
        const wornLimit = getWornLimitForSlot(slotName);
        if (wornOccupyingSlot >= wornLimit) {
            return false;
        }
    }
    return true;
}

const isLegalModForCharacter = (character: CharacterInitializer, mod: ModInitializer) => {
    for (let skillRequirement of mod.skillRequirements) {
        const skillLevel = getSkillLevel(character, skillRequirement.name);
        if (skillLevel < skillRequirement.level) {
            return false;
        }
    }
    return true;
}

const findItem = (character: CharacterInitializer, itemInstanceId: string) => {
    return getInventory(character).find(i => i.instanceId == itemInstanceId);
}

const findSlottingItem = (character: CharacterInitializer, allItems: ItemInitializer[], slotId: string) => {
    return getInventory(character).find(reference => {
        if (reference.customSlots.any(cs => cs.id == slotId)) {
            return true;
        }

        const item = allItems.find(i => i.id == reference.id);
        if (item == null) {
            return false;
        }
        return item.slots.any(s => s.id == slotId);
    });
}

const getArmor = (character: CharacterInitializer, allItems: ItemInitializer[]) => {
    return getEquipment(character).sum(ref => {
        const item = allItems.find(i => i.id == ref.id);
        return item?.armor ?? 0;
    });
}

const getResilience = (character: CharacterInitializer, allItems: ItemInitializer[]) => {
    return getEquipment(character).sum(ref => {
        const item = allItems.find(i => i.id == ref.id);
        return item?.resilience ?? 0;
    });
}

const getDefensiveItem = (character: CharacterInitializer, allItems: ItemInitializer[]) => {
    for (let ref of character.worn) {
        const item = allItems.first(i => i.id == ref.id);
        if (item?.wornOn.contains("Torso")) {
            return item;
        }
    }
    return null;
}

const getMovement = (character: CharacterInitializer) => {
    return character.movementOverride ?? (character.baseMovement + character.movementAdjust);
}

const getActions = (character: CharacterInitializer, allItems: ItemInitializer[], allPerks: PerkInitializer[], allSkills: SkillInitializer[]): CharacterAbility[] => {
    return [...character.held.mapMany(h => ItemHelpers.getActions(allItems, h, character.activeMods)),
        ...character.worn.mapMany(w => ItemHelpers.getActions(allItems, w, character.activeMods)),
        ...character.perks.mapMany(p => PerkHelpers.getActions(allPerks, p)),
        ...character.skills.mapMany(s => SkillHelpers.getActions(allSkills, s))];
}

const getReactions = (character: CharacterInitializer, allItems: ItemInitializer[], allPerks: PerkInitializer[], allSkills: SkillInitializer[]): CharacterAbility[] => {
    return [...character.held.mapMany(h => ItemHelpers.getReactions(allItems, h, character.activeMods)),
        ...character.worn.mapMany(w => ItemHelpers.getReactions(allItems, w, character.activeMods)),
        ...character.perks.mapMany(p => PerkHelpers.getReactions(allPerks, p)),
        ...character.skills.mapMany(s => SkillHelpers.getReactions(allSkills, s))];
}

const getPassives = (character: CharacterInitializer, allItems: ItemInitializer[], allPerks: PerkInitializer[], allSkills: SkillInitializer[]): CharacterAbility[] => {
    return [...character.held.mapMany(h => ItemHelpers.getPassives(allItems, h, character.activeMods)),
        ...character.worn.mapMany(w => ItemHelpers.getPassives(allItems, w, character.activeMods)),
        ...character.perks.mapMany(p => PerkHelpers.getPassives(allPerks, p)),
        ...character.skills.mapMany(s => SkillHelpers.getPassives(allSkills, s))];
}

const getFeatures = (character: CharacterInitializer, allPerks: PerkInitializer[], allSkills: SkillInitializer[]): CharacterAbility[] => {
    const perkFeatures = character.perks.mapMany(perk => PerkHelpers.getFeatures(allPerks, perk));
    const skillFeatures = character.skills.mapMany(skill => SkillHelpers.getFeatures(allSkills, skill));
    return [...perkFeatures, ...skillFeatures].unique(GetCharacterAbilityKey);
}

export const CharacterHelpers = Object.freeze({
    getSkillLevel,
    getMaxHordeSize,
    getRemainingSkillPoints,
    getMaxHealth,
    getEquipment,
    getInventory,
    searchItemByInstanceId,
    getItemCollection,
    removeItems,
    couldHoldItem,
    getWornLimitForSlot,
    couldWearItem,
    isLegalModForCharacter,
    findItem,
    findSlottingItem,
    getArmor,
    getResilience,
    getDefensiveItem,
    getMovement,
    getActions,
    getReactions,
    getPassives,
    getFeatures,

    MaxHands: 3,
    MinLevel: 1,
    MaxLevel: 10,
})

export type CharacterEntityReferences = {
    competencies: SortedSet<CompetencyInitializer>;
    conditions: SortedSet<Condition>;
    items: SortedSet<ItemInitializer>;
    minions: SortedSet<MinionInitializer>;
    perks: SortedSet<PerkInitializer>;
    personalities: SortedSet<Personality>;
    rhetorics: SortedSet<RhetoricInitializer>;
    skills: SortedSet<SkillInitializer>;
}

export type CharacterCollections = {
    competencies: CompetencyInitializer[];
    conditions: Condition[];
    items: ItemInitializer[];
    minions: MinionInitializer[];
    perks: PerkInitializer[];
    personalities: Personality[];
    rhetorics: RhetoricInitializer[];
    skills: SkillInitializer[];
}

export const CollectionsToReferences = (collections: CharacterCollections): CharacterEntityReferences => {
    return {
        competencies: new SortedSet(collections.competencies),
        conditions: new SortedSet(collections.conditions),
        items: new SortedSet(collections.items),
        minions: new SortedSet(collections.minions),
        perks: new SortedSet(collections.perks),
        personalities: new SortedSet(collections.personalities),
        rhetorics: new SortedSet(collections.rhetorics),
        skills: new SortedSet(collections.skills)
    };
}